import React, { useState, useEffect } from 'react';
import { 
  Code, 
  Copy, 
  ChevronDown,
  Palette,
  Settings,
  MessageSquare
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import { useToast } from '@/components/ui/Toast';
import ApiService from '@/services/api';

const Integration = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [settings, setSettings] = useState({
    theme: 'light',
    position: 'bottom-right'
  });
  const [copied, setCopied] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await ApiService.getIntegrationSettings();
      setSettings(prev => ({
        ...prev,
        theme: data.theme || 'light',
        position: data.widgetPosition || 'bottom-right'
      }));
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to fetch integration settings'
      });
    }
  };

  const saveSettings = async () => {
    try {
      setLoading(true);
      await ApiService.updateIntegrationSettings(settings);
      showToast({
        type: 'success',
        title: 'Success',
        message: 'Settings updated successfully'
      });
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to update settings'
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    if (!text) {
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Nothing to copy'
      });
      return;
    }

    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(''), 2000);
    showToast({
      type: 'success',
      title: 'Copied',
      message: 'Code copied to clipboard'
    });
  };

  const getIntegrationScript = () => {
    if (!user?.apiKey) return '';
    
    return `<!-- LocalFlow Widget -->
<script src="https://cdn.localflow.com/widget.js"></script>
<script>
  new LocalFlow('${user.apiKey}', {
    theme: '${settings.theme}',
    position: '${settings.position}'
  });
</script>`;
  };

  if (!user?.apiKey) {
    return (
      <Card className="p-6">
        <Alert variant="destructive">
          <AlertDescription>
            Unable to load integration details. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </Card>
    );
  }

  return (

    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Website Integration</h2>
        <Button 
          variant="outline" 
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2"
        >
          {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
          <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
        </Button>
      </div>

      {/* Quick Start Guide */}
      <Card className="p-6">
        <Alert className="mb-6">
          <AlertDescription>
            Add this code to your website just before the closing &lt;/body&gt; tag
          </AlertDescription>
        </Alert>

        <div className="bg-gray-900 rounded-lg p-4 relative">
          <pre className="text-white font-mono text-sm overflow-x-auto">
            {getIntegrationScript()}
          </pre>
          <Button 
            variant="outline" 
            size="sm"
            className="absolute top-2 right-2"
            onClick={() => copyToClipboard(getIntegrationScript(), 'script')}
          >
            <Copy className="w-4 h-4 mr-2" />
            {copied === 'script' ? 'Copied!' : 'Copy'}
          </Button>
        </div>

        {/* Preview */}
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-2">Widget Preview</h3>
          <div className="border rounded-lg p-4 bg-gray-50 relative h-[300px]">
            <div 
              className={`absolute ${
                settings.position === 'bottom-right' ? 'bottom-4 right-4' :
                settings.position === 'bottom-left' ? 'bottom-4 left-4' :
                settings.position === 'top-right' ? 'top-4 right-4' :
                'top-4 left-4'
              }`}
            >
              <Button className="rounded-full w-14 h-14 shadow-lg">
                <MessageSquare className="w-6 h-6" />
              </Button>
            </div>
            <div className="h-full flex items-center justify-center text-gray-400">
              Widget will appear here on your website
            </div>
          </div>
        </div>
      </Card>

      {/* Advanced Settings */}
      {showAdvanced && (
        <Card className="p-6">
          <div className="flex items-center space-x-2 mb-6">
            <Settings className="w-5 h-5" />
            <h3 className="text-lg font-semibold">Widget Settings</h3>
          </div>

          <div className="space-y-6">
            {/* Theme Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Theme</label>
              <select 
                className="w-full rounded-lg border-gray-300"
                value={settings.theme}
                onChange={(e) => setSettings(prev => ({ 
                  ...prev, 
                  theme: e.target.value 
                }))}
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="system">System (Follows user's preference)</option>
              </select>
            </div>

            {/* Position Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Widget Position</label>
              <select 
                className="w-full rounded-lg border-gray-300"
                value={settings.position}
                onChange={(e) => setSettings(prev => ({ 
                  ...prev, 
                  position: e.target.value 
                }))}
              >
                <option value="bottom-right">Bottom Right</option>
                <option value="bottom-left">Bottom Left</option>
                <option value="top-right">Top Right</option>
                <option value="top-left">Top Left</option>
              </select>
            </div>

            {/* Save Settings Button */}
            <Button 
              className="w-full"
              onClick={saveSettings}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Integration;